import {
  ConfirmationMessageTypeEnum,
  type ExternalToolResult,
  type ExternalToolResultItem,
  type ExternalToolResultsMessage,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import type { ExternalToolBatch } from './external-tool-batch.types';
import { getRequiredToolCallIds } from './external-tool-batch-gates';

export const globalCompletedExternalToolResultsByBatchAndToolCallId = new Map<
  string,
  ExternalToolResult
>();

const recentlyFlushedExternalToolResultsByToolCallId = new Map<
  string,
  ExternalToolResult
>();

const successfullyDeliveredExternalToolResultsByToolCallId = new Set<string>();
const successfullyDeliveredExternalToolResultOrder: string[] = [];

const MAX_COMPLETED_EXTERNAL_TOOL_RESULTS = 500;
const MAX_RECENTLY_FLUSHED_EXTERNAL_TOOL_RESULTS = 500;
const MAX_SUCCESSFULLY_DELIVERED_EXTERNAL_TOOL_RESULTS = 500;

export function rememberCompletedExternalToolResult(
  result: ExternalToolResult,
): void {
  globalCompletedExternalToolResultsByBatchAndToolCallId.set(
    getCompletedResultCacheKey({
      batchId: result.batchId,
      chatId: result.chatId,
      toolCallId: result.toolCallId,
    }),
    result,
  );

  while (
    globalCompletedExternalToolResultsByBatchAndToolCallId.size >
    MAX_COMPLETED_EXTERNAL_TOOL_RESULTS
  ) {
    const oldestKey = globalCompletedExternalToolResultsByBatchAndToolCallId
      .keys()
      .next().value;

    if (!oldestKey) {
      break;
    }

    globalCompletedExternalToolResultsByBatchAndToolCallId.delete(oldestKey);
  }
}

export function dropAlreadyDeliveredBatchResults({
  batch,
  batchId,
}: {
  batch: ExternalToolBatch;
  batchId: string;
}): void {
  const delivered = [...batch.results.values()].filter(
    (result) =>
      wasExternalToolResultSuccessfullyDelivered({
        batchId: result.batchId,
        chatId: result.chatId,
        toolCallId: result.toolCallId,
      }) ||
      wasExternalToolResultSuccessfullyDelivered({
        batchId,
        chatId: batch.chatId,
        toolCallId: result.toolCallId,
      }),
  );

  if (delivered.length === 0) {
    return;
  }

  removeFlushedResultsFromBatch({
    batch,
    batchId,
    flushedResults: delivered,
  });
}

export function getCompletedResultCacheKey({
  batchId,
  chatId,
  toolCallId,
}: {
  batchId: string;
  chatId: string;
  toolCallId: string;
}): string {
  return `${chatId}:${batchId}:${toolCallId}`;
}

export function hasRecentlyFlushedResult({
  batchId,
  chatId,
  toolCallId,
}: {
  batchId: string;
  chatId: string;
  toolCallId: string;
}): boolean {
  const cacheKey = getCompletedResultCacheKey({
    batchId,
    chatId,
    toolCallId,
  });

  if (successfullyDeliveredExternalToolResultsByToolCallId.has(cacheKey)) {
    return false;
  }

  return recentlyFlushedExternalToolResultsByToolCallId.has(cacheKey);
}

export function applyCachedResults({
  batch,
  batchId,
  completedResultsByToolCallId,
}: {
  batch: ExternalToolBatch;
  batchId: string;
  completedResultsByToolCallId: Map<string, ExternalToolResult>;
}): number {
  let appliedCount = 0;

  for (const result of completedResultsByToolCallId.values()) {
    if (result.batchId !== batchId || result.chatId !== batch.chatId) {
      continue;
    }

    if (!batch.requests.has(result.toolCallId)) {
      continue;
    }

    if (batch.results.has(result.toolCallId)) {
      continue;
    }

    batch.results.set(result.toolCallId, result);
    appliedCount += 1;
  }

  for (const toolCallId of getRequiredToolCallIds(batch)) {
    if (batch.results.has(toolCallId)) {
      continue;
    }

    const cached = completedResultsByToolCallId.get(
      getCompletedResultCacheKey({
        batchId,
        chatId: batch.chatId,
        toolCallId,
      }),
    );

    if (!cached) {
      continue;
    }

    batch.results.set(toolCallId, cached);
    appliedCount += 1;
  }

  for (const toolCallId of batch.pendingExecuteToolCallIds) {
    if (batch.results.has(toolCallId)) {
      continue;
    }

    const cached = completedResultsByToolCallId.get(
      getCompletedResultCacheKey({
        batchId,
        chatId: batch.chatId,
        toolCallId,
      }),
    );

    if (!cached) {
      continue;
    }

    if (!batch.requests.has(toolCallId)) {
      batch.requests.set(toolCallId, {
        batchId,
        chatId: batch.chatId,
        toolCallId,
        toolName: cached.toolName,
      });
    }

    batch.results.set(toolCallId, cached);
    batch.pendingExecuteToolCallIds.delete(toolCallId);
    appliedCount += 1;
  }

  return appliedCount;
}

export function getFlushResults({
  batch,
  batchId,
  completedResultsByToolCallId,
  toolCallIdsToFlush,
}: {
  batch: ExternalToolBatch;
  batchId: string;
  completedResultsByToolCallId: Map<string, ExternalToolResult>;
  toolCallIdsToFlush: string[];
}): ExternalToolResult[] {
  const resultsByToolCallId = new Map<string, ExternalToolResult>();

  for (const result of completedResultsByToolCallId.values()) {
    if (result.batchId === batchId && result.chatId === batch.chatId) {
      resultsByToolCallId.set(result.toolCallId, result);
    }
  }

  for (const [toolCallId, result] of batch.results) {
    resultsByToolCallId.set(toolCallId, result);
  }

  return toolCallIdsToFlush
    .map((toolCallId) => resultsByToolCallId.get(toolCallId))
    .filter((result): result is ExternalToolResult => result !== undefined);
}

export function clearCompletedExternalToolResultsCache(): void {
  globalCompletedExternalToolResultsByBatchAndToolCallId.clear();
  recentlyFlushedExternalToolResultsByToolCallId.clear();
  successfullyDeliveredExternalToolResultsByToolCallId.clear();
  successfullyDeliveredExternalToolResultOrder.length = 0;
}

export function pruneSuccessfullyDeliveredExternalToolResults(): void {
  while (
    successfullyDeliveredExternalToolResultOrder.length >
    MAX_SUCCESSFULLY_DELIVERED_EXTERNAL_TOOL_RESULTS
  ) {
    const oldestKey = successfullyDeliveredExternalToolResultOrder.shift();

    if (!oldestKey) {
      break;
    }

    successfullyDeliveredExternalToolResultsByToolCallId.delete(oldestKey);
  }
}

export function markSuccessfullyDeliveredResults(
  results: ExternalToolResult[],
): void {
  for (const result of results) {
    const cacheKey = getCompletedResultCacheKey({
      batchId: result.batchId,
      chatId: result.chatId,
      toolCallId: result.toolCallId,
    });

    if (!successfullyDeliveredExternalToolResultsByToolCallId.has(cacheKey)) {
      successfullyDeliveredExternalToolResultOrder.push(cacheKey);
    }

    successfullyDeliveredExternalToolResultsByToolCallId.add(cacheKey);
  }

  pruneSuccessfullyDeliveredExternalToolResults();
}

export function wasExternalToolResultSuccessfullyDelivered({
  batchId,
  chatId,
  toolCallId,
}: {
  batchId: string;
  chatId: string;
  toolCallId: string;
}): boolean {
  return successfullyDeliveredExternalToolResultsByToolCallId.has(
    getCompletedResultCacheKey({
      batchId,
      chatId,
      toolCallId,
    }),
  );
}

export function rememberRecentlyFlushedResults(
  results: ExternalToolResult[],
): void {
  for (const result of results) {
    recentlyFlushedExternalToolResultsByToolCallId.set(
      getCompletedResultCacheKey({
        batchId: result.batchId,
        chatId: result.chatId,
        toolCallId: result.toolCallId,
      }),
      result,
    );
  }

  while (
    recentlyFlushedExternalToolResultsByToolCallId.size >
    MAX_RECENTLY_FLUSHED_EXTERNAL_TOOL_RESULTS
  ) {
    const oldestKey = recentlyFlushedExternalToolResultsByToolCallId
      .keys()
      .next().value;

    if (!oldestKey) {
      break;
    }

    recentlyFlushedExternalToolResultsByToolCallId.delete(oldestKey);
  }
}

export function getExternalToolResultForRetry({
  batchId,
  chatId,
  toolCallId,
}: {
  batchId: string;
  chatId: string;
  toolCallId: string;
}): ExternalToolResult | undefined {
  if (
    wasExternalToolResultSuccessfullyDelivered({
      batchId,
      chatId,
      toolCallId,
    })
  ) {
    return undefined;
  }

  const cacheKey = getCompletedResultCacheKey({
    batchId,
    chatId,
    toolCallId,
  });

  return (
    globalCompletedExternalToolResultsByBatchAndToolCallId.get(cacheKey) ??
    recentlyFlushedExternalToolResultsByToolCallId.get(cacheKey)
  );
}

export function clearCompletedExternalToolResultsForToolCall({
  batchId,
  chatId,
  toolCallId,
}: {
  batchId: string;
  chatId: string;
  toolCallId: string;
}): void {
  globalCompletedExternalToolResultsByBatchAndToolCallId.delete(
    getCompletedResultCacheKey({
      batchId,
      chatId,
      toolCallId,
    }),
  );
}

export function clearCompletedExternalToolResultsForChat(chatId: string): void {
  for (const [
    cacheKey,
    result,
  ] of globalCompletedExternalToolResultsByBatchAndToolCallId) {
    if (result.chatId === chatId) {
      globalCompletedExternalToolResultsByBatchAndToolCallId.delete(cacheKey);
    }
  }

  for (const [
    cacheKey,
    result,
  ] of recentlyFlushedExternalToolResultsByToolCallId) {
    if (result.chatId === chatId) {
      recentlyFlushedExternalToolResultsByToolCallId.delete(cacheKey);
    }
  }

  for (const cacheKey of [
    ...successfullyDeliveredExternalToolResultsByToolCallId,
  ]) {
    if (cacheKey.startsWith(`${chatId}:`)) {
      successfullyDeliveredExternalToolResultsByToolCallId.delete(cacheKey);
      const orderIndex =
        successfullyDeliveredExternalToolResultOrder.indexOf(cacheKey);

      if (orderIndex >= 0) {
        successfullyDeliveredExternalToolResultOrder.splice(orderIndex, 1);
      }
    }
  }
}

export function removeFlushedResultsFromBatch({
  batch,
  batchId,
  flushedResults,
}: {
  batch: ExternalToolBatch;
  batchId: string;
  flushedResults: ExternalToolResult[];
}): void {
  for (const result of flushedResults) {
    batch.requests.delete(result.toolCallId);
    batch.results.delete(result.toolCallId);
    batch.pendingExecuteToolCallIds.delete(result.toolCallId);
    batch.expectedToolCallIds.delete(result.toolCallId);
    batch.lostExecuteToolCallIds.delete(result.toolCallId);
    clearCompletedExternalToolResultsForToolCall({
      batchId: result.batchId,
      chatId: result.chatId,
      toolCallId: result.toolCallId,
    });

    if (result.batchId !== batchId) {
      clearCompletedExternalToolResultsForToolCall({
        batchId,
        chatId: batch.chatId,
        toolCallId: result.toolCallId,
      });
    }
  }
}

export function isBatchEmpty(batch: ExternalToolBatch): boolean {
  return (
    batch.requests.size === 0 &&
    batch.results.size === 0 &&
    batch.pendingExecuteToolCallIds.size === 0 &&
    batch.lostExecuteToolCallIds.size === 0 &&
    batch.expectedToolCallIds.size === 0
  );
}

export function buildExternalToolResultsMessage({
  batchId,
  results,
}: {
  batchId: string;
  results: ExternalToolResult[];
}): TerraUIMessage & { metadata: ExternalToolResultsMessage } {
  return {
    id: `external-tool-results-${batchId}-${Date.now()}`,
    role: 'user',
    parts: [],
    metadata: {
      type: ConfirmationMessageTypeEnum.ExternalToolResults,
      batchId,
      results: results.map(
        ({
          batchId: resultBatchId,
          toolCallId,
          toolName,
          status,
          result,
        }): ExternalToolResultItem => ({
          batchId: resultBatchId,
          toolCallId,
          toolName,
          status,
          result,
        }),
      ),
    },
  };
}

export async function deliverOrphanExternalToolResult({
  sendMessage,
  result,
}: {
  sendMessage: (chatId: string, message: TerraUIMessage) => Promise<void>;
  result: ExternalToolResult;
}): Promise<void> {
  try {
    await sendMessage(
      result.chatId,
      buildExternalToolResultsMessage({
        batchId: result.batchId,
        results: [result],
      }),
    );
    rememberRecentlyFlushedResults([result]);
    markSuccessfullyDeliveredResults([result]);
  } catch (error: unknown) {
    reportBackgroundFlushError(error);
  }
}

export function reportBackgroundFlushError(error: unknown): void {
  console.warn(
    '[ExternalToolResultCoordinator] Failed to flush ready external tool batch',
    error,
  );
}
